// RUTA: src/components/fitness/NutritionHistorySection.tsx
//
// Heatmap 12 semanas de adherencia calórica para el cliente.
// Gemelo del CardioHistorySection — mismo lenguaje visual para que
// el cliente no tenga que aprender dos UIs distintas.
//
// El heatmap es solo NAVEGACIÓN: tap en una celda actualiza el
// `selectedDate` que el padre usa para mostrar el detalle completo
// (anillo, macros, avatar, lista de comidas) ARRIBA de la sección.
// Así evitamos duplicar la UI de comidas.
//
// Filosofía: la meta es FIJA (no eat-back). Las kcal de cardio NO se
// suman aquí — viven en su propia sección.

import { useEffect, useMemo, useState } from 'react';
import { Apple, Target, Flame, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cachedQuery } from '@/lib/offlineStorage';
import { useReconnectRefetch } from '@/hooks/useReconnectRefetch';

interface FoodLogRow {
  id: number;
  item_name: string;
  calories: number;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  meal_type: string;
  log_date: string;
}

interface Props {
  userId: string;
  profileName: string;
  /** Meta diaria de kcal */
  dailyGoal: number;
  /** Trigger de refresh tras log */
  refreshKey?: number;
  /** Si true, oculta acciones de borrar (vista coach) */
  readOnly?: boolean;
  /** Día seleccionado actualmente. Si se pasa, el heatmap es controlado
   *  por el padre y al hacer click en una celda se invoca onSelectDate.
   *  Si no se pasa, mantiene estado interno. */
  selectedDate?: string | null;
  onSelectDate?: (date: string | null) => void;
}

const WEEKS = 12;
const TOTAL_DAYS = WEEKS * 7;
const DAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function formatLocalDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

type Band = 'none' | 'on-target' | 'close' | 'off';

function bandFor(kcal: number, goal: number): Band {
  if (kcal === 0) return 'none';
  if (goal <= 0) return 'on-target';
  const pct = (kcal / goal) * 100;
  if (pct >= 85 && pct <= 115) return 'on-target';
  if ((pct >= 70 && pct < 85) || (pct > 115 && pct <= 130)) return 'close';
  return 'off';
}

function colorForBand(band: Band): string {
  switch (band) {
    case 'on-target': return 'bg-green-500 border-green-600';
    case 'close':     return 'bg-yellow-500/70 border-yellow-500/80';
    case 'off':       return 'bg-orange-500/60 border-orange-500/70';
    default:          return 'bg-muted/40 border-border/40';
  }
}

export function NutritionHistorySection({ userId, profileName, dailyGoal, refreshKey, readOnly = false, selectedDate, onSelectDate }: Props) {
  const [logs, setLogs] = useState<FoodLogRow[]>([]);
  const [internalPinned, setInternalPinned] = useState<string | null>(null);
  // Modo controlado si el padre pasa selectedDate, si no, estado interno
  const isControlled = selectedDate !== undefined;
  const pinnedDate = isControlled ? selectedDate : internalPinned;
  const setPinnedDate = (next: string | null) => {
    if (isControlled) onSelectDate?.(next);
    else setInternalPinned(next);
  };

  const fetchHistory = () => {
    if (!userId) return;
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - (TOTAL_DAYS - 1));
    const startStr = formatLocalDate(start);
    // Cacheado para que offline siga mostrando el heatmap nutricional
    // del cliente con sus últimos N días en vez de quedarse vacío.
    cachedQuery<FoodLogRow[]>(
      `nutrition_history_${userId}_${profileName}_${startStr}`,
      () => supabase
        .from('food_logs')
        .select('id, item_name, calories, protein, carbs, fat, meal_type, log_date')
        .eq('user_id', userId)
        .eq('profile_name', profileName)
        .gte('log_date', startStr)
        .order('log_date', { ascending: false })
        .order('created_at', { ascending: true })
    ).then(({ data }) => {
      setLogs((data || []) as FoodLogRow[]);
    });
  };
  useEffect(() => { fetchHistory(); }, [userId, profileName, refreshKey]);
  useReconnectRefetch(fetchHistory);

  const byDate = useMemo(() => {
    const map: Record<string, FoodLogRow[]> = {};
    for (const l of logs) (map[l.log_date] ||= []).push(l);
    return map;
  }, [logs]);

  const grid = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days: { date: string; kcal: number; band: Band }[] = [];
    for (let i = TOTAL_DAYS - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateStr = formatLocalDate(d);
      const sessions = byDate[dateStr] || [];
      const kcal = sessions.reduce((s, x) => s + Number(x.calories), 0);
      days.push({ date: dateStr, kcal, band: bandFor(kcal, dailyGoal) });
    }
    const columns: typeof days[] = [];
    let currentCol: typeof days = [];
    for (const day of days) {
      const dayOfWeek = (new Date(day.date + 'T12:00:00').getDay() + 6) % 7;
      if (currentCol.length === 0 && dayOfWeek !== 0 && columns.length === 0) {
        for (let p = 0; p < dayOfWeek; p++) currentCol.push({ date: '', kcal: 0, band: 'none' });
      }
      currentCol.push(day);
      if (currentCol.length === 7 || dayOfWeek === 6) {
        while (currentCol.length < 7) currentCol.push({ date: '', kcal: 0, band: 'none' });
        columns.push(currentCol);
        currentCol = [];
      }
    }
    if (currentCol.length > 0) {
      while (currentCol.length < 7) currentCol.push({ date: '', kcal: 0, band: 'none' });
      columns.push(currentCol);
    }
    return columns;
  }, [byDate, dailyGoal]);

  // Stats: este mes (kcal promedio, días en meta, días registrados)
  const monthStats = useMemo(() => {
    const now = new Date();
    const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const dayOfMonth = now.getDate();
    const monthLogs = logs.filter(l => l.log_date >= firstOfMonth);
    const dayKcal: Record<string, number> = {};
    for (const l of monthLogs) dayKcal[l.log_date] = (dayKcal[l.log_date] || 0) + Number(l.calories);
    const distinctDays = Object.keys(dayKcal).length;
    const inGoalDays = Object.values(dayKcal).filter(k => bandFor(k, dailyGoal) === 'on-target').length;
    const totalKcal = Object.values(dayKcal).reduce((s, k) => s + k, 0);
    const avgKcal = distinctDays > 0 ? Math.round(totalKcal / distinctDays) : 0;
    return { distinctDays, inGoalDays, avgKcal, dayOfMonth };
  }, [logs, dailyGoal]);

  const pinnedFoodLogs = pinnedDate ? (byDate[pinnedDate] || []) : [];

  return (
    <section className="rounded-xl border-2 border-border bg-card p-4 md:p-5">
      <div className="flex items-center gap-2 mb-4">
        <Apple className="h-5 w-5 text-accent" />
        <h3 className="text-sm font-black uppercase tracking-widest">Historial de Calorías</h3>
        <span className="text-[10px] text-muted-foreground font-bold ml-auto">{WEEKS} semanas</span>
      </div>

      {/* Stats del mes */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="p-3 rounded-lg bg-secondary/40 border">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
            <Apple className="h-3 w-3" /> Días registrando
          </div>
          <div className="text-base font-black">
            {monthStats.distinctDays}
            <span className="text-[10px] text-muted-foreground font-bold"> / {monthStats.dayOfMonth}</span>
          </div>
        </div>
        <div className="p-3 rounded-lg bg-secondary/40 border">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
            <Target className="h-3 w-3" /> En meta
          </div>
          <div className="text-base font-black">
            {monthStats.inGoalDays}
            <span className="text-[10px] text-muted-foreground font-bold"> días</span>
          </div>
        </div>
        <div className="p-3 rounded-lg bg-secondary/40 border">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
            <Flame className="h-3 w-3" /> Promedio
          </div>
          <div className="text-base font-black">
            {monthStats.avgKcal.toLocaleString()}
            <span className="text-[10px] text-muted-foreground font-bold"> kcal</span>
          </div>
        </div>
      </div>

      {/* Heatmap */}
      <div className="flex gap-2 items-start overflow-x-auto pb-2">
        <div className="flex flex-col gap-1 pt-0.5 shrink-0">
          {DAY_LABELS.map(label => (
            <div key={label} className="h-4 text-[10px] text-muted-foreground flex items-center pr-1">
              {label}
            </div>
          ))}
        </div>
        <div className="flex gap-1 shrink-0">
          {grid.map((col, ci) => (
            <div key={ci} className="flex flex-col gap-1">
              {col.map((cell, ri) => (
                <div
                  key={ri}
                  onClick={() => {
                    if (!cell.date) return;
                    setPinnedDate(pinnedDate === cell.date ? null : cell.date);
                  }}
                  className={`w-4 h-4 rounded-sm border ${cell.date ? colorForBand(cell.band) + ` cursor-pointer hover:ring-2 hover:ring-accent transition-all ${pinnedDate === cell.date ? 'ring-2 ring-accent' : ''}` : 'opacity-0 pointer-events-none'}`}
                  title={cell.date ? `${cell.date}: ${cell.kcal} kcal` : ''}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[10px] text-muted-foreground">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-muted/40 border border-border/40" /> Sin registro
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-green-500 border-green-600" /> En meta (85–115%)
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-yellow-500/70 border-yellow-500/80" /> Cerca
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-orange-500/60 border-orange-500/70" /> Lejos
        </div>
      </div>

      {/* Confirmación visual del día seleccionado.
       *  El detalle completo (anillo, macros, avatar, lista de
       *  comidas) ya se muestra ARRIBA de la sección al picar un día
       *  — el padre hace scroll suave hacia arriba. Aquí solo
       *  confirmamos qué celda está activa para que el cliente vea
       *  sin ambigüedad qué día está viendo. */}
      {pinnedDate && (
        <div className="mt-3 p-3 rounded-lg bg-accent/5 border-2 border-accent/30 text-sm relative flex items-center gap-2">
          <button
            onClick={() => setPinnedDate(null)}
            className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
            aria-label="Cerrar selección"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-accent">
              Mostrando arriba
            </div>
            <div className="font-bold text-sm truncate">
              {new Date(pinnedDate + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {' · '}
              <span className="text-accent">
                {pinnedFoodLogs.reduce((s, l) => s + Number(l.calories), 0).toLocaleString()} kcal
              </span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
